import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

const Reconocimiento = () => window.SpeechRecognition || window.webkitSpeechRecognition;
const LOCALES = { es: "es-ES", en: "en-US", fr: "fr-FR", de: "de-DE", it: "it-IT", nl: "nl-NL", ar: "ar-SA" };

// La Web Speech API no detecta sola el idioma hablado: usa el idioma de la web (o del navegador) y, si no es uno de los nuestros, español.
export const idiomaVoz = (lng) => LOCALES[(lng || "es").split("-")[0]] || "es-ES";

const ERRORES = {
  "not-allowed": "Permite el acceso al micrófono en tu navegador para poder hablar con el asistente.",
  "service-not-allowed": "Permite el acceso al micrófono en tu navegador para poder hablar con el asistente.",
  "audio-capture": "No se ha encontrado ningún micrófono.",
  network: "El reconocimiento de voz necesita conexión a internet.",
};

// Una frase por pulsación: el navegador corta solo al detectar silencio (o al volver a pulsar) y se envía lo dicho.
// Con «sensible» (Nuvli) se da la frase por terminada tras PAUSA_MS de silencio y se envía al momento.
const PAUSA_MS = 800;
export function useVozAsistente({ lang, onTexto, onFin, sensible = false }) {
  const [grabando, setGrabando] = useState(false);
  const rec = useRef(null);
  const dicho = useRef("");
  const pausa = useRef(null);
  const auto = useRef(false);
  const cb = useRef({ onTexto, onFin });
  cb.current = { onTexto, onFin };
  useEffect(() => () => rec.current?.abort(), []);

  const empezar = (base) => {
    const R = Reconocimiento();
    if (!R) { toast.error("Tu navegador no soporta voz, prueba con Chrome o Safari"); return; }
    const r = new R();
    r.lang = lang;
    r.continuous = sensible;
    r.interimResults = true;
    r.maxAlternatives = 1;
    dicho.current = "";
    const terminar = () => {
      clearTimeout(pausa.current);
      const t = dicho.current;
      dicho.current = "";
      if (t) cb.current.onFin(`${base}${t}`);
    };
    r.onresult = (e) => {
      dicho.current = Array.from(e.results).map((x) => x[0].transcript).join(" ").replace(/\s+/g, " ").trim();
      cb.current.onTexto(`${base}${dicho.current}`);
      if (sensible) {
        clearTimeout(pausa.current);
        pausa.current = setTimeout(() => { r.onresult = null; terminar(); r.abort(); }, PAUSA_MS);
      }
    };
    r.onerror = (e) => {
      if (ERRORES[e.error]) toast.error(ERRORES[e.error]);
      else if (e.error === "no-match") toast.info("No te he entendido. ¿Puedes repetirlo?");
      else if (e.error === "no-speech" && !auto.current) toast.info("No te he oído. Pulsa el micrófono e inténtalo de nuevo.");
    };
    r.onend = () => {
      rec.current = null;
      setGrabando(false);
      terminar();
    };
    rec.current = r;
    try { r.start(); setGrabando(true); } catch { rec.current = null; toast.error("No se pudo iniciar el micrófono."); }
  };

  // «seguir»: reactivado solo tras una respuesta; si no se dice nada se apaga sin avisar.
  const alternar = (base, seguir = false) => {
    if (rec.current) { rec.current.stop(); return; }
    auto.current = seguir;
    empezar(base);
  };
  return { grabando, alternar };
}
